import { useState } from "react";
import { Users, Crown, Medal, Award, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface Participant {
  id: string;
  name: string;
  partner?: string;
  avatar?: string;
  registrationDate: string;
  isCurrentUser?: boolean;
  category?: string;
}

interface ParticipantListProps {
  participants?: Participant[];
  maxParticipants?: number;
  gameType: 'singles' | 'doubles';
  eventType: 'tournament' | 'league';
  eventTitle: string;
  className?: string;
}

const ParticipantList = ({ 
  participants = [],
  maxParticipants = 16,
  gameType,
  eventType,
  eventTitle,
  className = ""
}: ParticipantListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Mock participants data
  const mockParticipants: Participant[] = [
    {
      id: "1",
      name: "TÚ",
      partner: gameType === 'doubles' ? "Ana López" : undefined,
      registrationDate: "2025-07-20",
      isCurrentUser: true,
      category: "A"
    },
    {
      id: "2", 
      name: "Carlos Rivera",
      partner: gameType === 'doubles' ? "Laura Sánchez" : undefined,
      registrationDate: "2025-07-21",
      category: "A"
    },
    {
      id: "3",
      name: "Diego López", 
      partner: gameType === 'doubles' ? "Carmen Ruiz" : undefined,
      registrationDate: "2025-07-22",
      category: "A"
    },
    {
      id: "4",
      name: "Roberto Silva",
      partner: gameType === 'doubles' ? "Elena Torres" : undefined, 
      registrationDate: "2025-07-22",
      category: "A"
    },
    {
      id: "5",
      name: "Fernando Mora",
      partner: gameType === 'doubles' ? "Isabel Vega" : undefined,
      registrationDate: "2025-07-23",
      category: "A"
    },
    {
      id: "6",
      name: "Miguel Castro",
      partner: gameType === 'doubles' ? "Sofía Herrera" : undefined,
      registrationDate: "2025-07-23",
      category: "A"
    }
  ];

  const currentParticipants = participants.length > 0 ? participants : mockParticipants;
  const registeredCount = gameType === 'doubles' ? currentParticipants.length : currentParticipants.length;
  const spotsRemaining = maxParticipants - registeredCount;

  const getParticipantIcon = (index: number) => {
    if (index === 0) return <Crown className="h-4 w-4 text-yellow-600" />;
    if (index === 1) return <Medal className="h-4 w-4 text-gray-500" />;
    if (index === 2) return <Award className="h-4 w-4 text-amber-600" />;
    return <User className="h-4 w-4 text-slate-400" />;
  };

  const getRegistrationBadge = (participant: Participant) => {
    if (participant.isCurrentUser) {
      return (
        <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/30">
          TÚ
        </Badge>
      );
    }
    return null;
  };

  const formatRegistrationDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'short'
    });
  };

  const displayedParticipants = isExpanded ? currentParticipants : currentParticipants.slice(0, 6);

  return (
    <Card className={`bg-gradient-to-br from-slate-50 to-indigo-50 border-slate-200 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-600" />
            <span className="text-lg font-bold text-slate-700">
              Participantes Inscritos
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-white/50 text-slate-600 border-slate-300">
              {registeredCount}/{maxParticipants}
            </Badge>
            {spotsRemaining > 0 && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                {spotsRemaining} cupos disponibles
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-center">
          <p className="text-sm text-slate-600">
            {eventType === 'tournament' ? 'Participantes confirmados para el torneo' : 'Miembros de la liga'}
          </p>
        </div>

        <div className="space-y-3">
          {displayedParticipants.map((participant, index) => (
            <div 
              key={participant.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                participant.isCurrentUser 
                  ? 'bg-primary/5 border-primary/20' 
                  : 'bg-white/50 border-slate-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  {getParticipantIcon(index)}
                  <span className="text-sm font-medium text-slate-600">#{index + 1}</span>
                </div>
                
                <Avatar className="h-8 w-8">
                  <AvatarImage src={participant.avatar} />
                  <AvatarFallback className="text-xs bg-slate-200 text-slate-600">
                    {participant.name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${
                      participant.isCurrentUser ? 'text-primary' : 'text-slate-700'
                    }`}>
                      {gameType === 'doubles' && participant.partner 
                        ? `${participant.name} / ${participant.partner}`
                        : participant.name
                      }
                    </span>
                    {getRegistrationBadge(participant)}
                  </div>
                  <span className="text-xs text-slate-500">
                    Inscrito: {formatRegistrationDate(participant.registrationDate)}
                  </span>
                </div>
              </div>

              {participant.category && (
                <Badge variant="outline" className="text-xs text-slate-600 border-slate-300">
                  Cat. {participant.category}
                </Badge>
              )}
            </div>
          ))}
        </div>

        {currentParticipants.length > 6 && (
          <div className="text-center pt-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-slate-600 border-slate-300 hover:bg-slate-50"
            >
              {isExpanded ? 'Ver menos' : `Ver todos (${currentParticipants.length - 6} más)`}
            </Button>
          </div>
        )}

        {spotsRemaining > 0 && (
          <div className="text-center pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              ¡Aún quedan <span className="font-medium text-green-600">{spotsRemaining} cupos</span> disponibles!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ParticipantList;